import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { productCountsAPI } from '../services/garageApi';
import TenantSwitcher from './TenantSwitcher';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Users, Package, ShoppingCart, Settings, LogOut, Menu, X,
  Car, Battery, Wrench, MapPin, UserCheck, Truck, FileText, BarChart3,
  Tag, Droplets, ClipboardList, Receipt, DollarSign, ChevronDown,
  ChevronRight, Bell, Search, TrendingUp, RotateCcw, Archive,
  Warehouse, Shield, AlertCircle, Building2, Grid3X3
} from 'lucide-react';

const EnhancedDashboardLayout = ({ children, currentPage = 'Dashboard' }) => {
  const { user: authUser, logout } = useAuth();
  const { hasActiveTenantContext } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(() => {
    const saved = localStorage.getItem('expandedMenus');
    return saved ? JSON.parse(saved) : {};
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [productCounts, setProductCounts] = useState({ tires: 0, batteries: 0, wipers: 0 });

  // Check if super admin needs to select a tenant
  useEffect(() => {
    if (authUser?.super_admin && !hasActiveTenantContext) {
      // Only show warning on pages that require tenant context
      const exemptPaths = ['/super-admin', '/tenant', '/login', '/'];
      const currentPath = location.pathname;
      
      if (!exemptPaths.includes(currentPath) && !currentPath.startsWith('/super-admin')) {
        console.log('Super admin needs to select tenant for this page');
      }
    }
  }, [authUser, hasActiveTenantContext, location]);

  // Fetch product counts for sidebar badges
  useEffect(() => {
    if (hasActiveTenantContext) {
      productCountsAPI.getCounts()
        .then(data => {
          if (data && typeof data === 'object') {
            setProductCounts(data);
          }
        })
        .catch(() => {});
    }
  }, [hasActiveTenantContext]);

  useEffect(() => {
    localStorage.setItem('expandedMenus', JSON.stringify(expandedMenus));
  }, [expandedMenus]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-dropdown')) {
        setShowUserMenu(false);
      }
      if (showNotifications && !event.target.closest('.notifications-dropdown')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu, showNotifications]);

  const user = authUser || { name: 'Admin', email: 'admin@rubbert.com' };

  const navigationItems = [
    {
      name: 'Dashboard',
      icon: Home,
      href: '/',
      current: currentPage === 'Dashboard',
      badge: null
    },
    {
      name: 'User Management',
      icon: Users,
      badge: '2',
      children: [
        { name: 'Suppliers', href: '/suppliers', icon: Truck },
        { name: 'Customers', href: '/customers', icon: UserCheck }
      ]
    },
    {
      name: 'Tenants',
      icon: Building2,
      href: '/tenants',
      current: currentPage === 'Tenants',
      requireSuperAdmin: true
    },
    {
      name: 'Product Management',
      icon: Package,
      children: [
        { name: 'Tire Brands', href: '/tire-brands', icon: Car },
        { name: 'Battery Brands', href: '/battery-brands', icon: Battery },
        { name: 'Patterns', href: '/patterns', icon: Wrench },
        { name: 'Tags', href: '/tags', icon: Tag },
        { name: 'Tire Types', href: '/tire-types', icon: Car },
        { name: 'Battery Types', href: '/battery-types', icon: Battery },
        { name: 'Locations', href: '/locations', icon: MapPin },
        { name: 'Zones', href: '/zones', icon: Grid3X3 }
      ]
    },
    {
      name: 'Inventory',
      icon: Warehouse,
      badge: 'Low',
      badgeType: 'warning',
      children: [
        { name: 'Overview', href: '/inventories', icon: Archive },
        { name: 'Tires', href: '/tires', icon: Car, badge: productCounts.tires > 0 ? String(productCounts.tires) : null },
        { name: 'Batteries', href: '/batteries', icon: Battery, badge: productCounts.batteries > 0 ? String(productCounts.batteries) : null },
        { name: 'Wipers', href: '/wiper-products', icon: Droplets, badge: productCounts.wipers > 0 ? String(productCounts.wipers) : null },
        { name: 'Inventory Movements', href: '/inventory-movements', icon: TrendingUp },
        { name: 'Stock Adjustments', href: '/stock-adjustments', icon: RotateCcw }
      ]
    },
    {
      name: 'Sales',
      icon: ShoppingCart,
      badge: '5',
      badgeType: 'success',
      children: [
        { name: 'Invoices', href: '/invoices', icon: FileText },
        { name: 'Sales', href: '/sales', icon: DollarSign },
        { name: 'Receipts', href: '/receipts?type=sale', icon: Receipt }
      ]
    },
    {
      name: 'Purchases',
      icon: Truck,
      children: [
        { name: 'Orders', href: '/purchases', icon: ClipboardList },
        { name: 'Receipts', href: '/receipts?type=purchase', icon: Receipt }
      ]
    },
    {
      name: 'Reports',
      icon: BarChart3,
      children: [
        { name: 'Inventory', href: '/reports/inventory', icon: Package },
        { name: 'Sales', href: '/reports/sales', icon: ShoppingCart },
        { name: 'Purchases', href: '/reports/purchases', icon: Truck },
        { name: 'Customers', href: '/reports/customer-balance', icon: Users },
        { name: 'Suppliers', href: '/reports/supplier-balance', icon: Truck }
      ]
    },
    {
      name: 'Settings',
      icon: Settings,
      children: [
        { name: 'Tenant Management', href: '/tenants', icon: Building2, requireSuperAdmin: true },
        {
          name: 'Super Admin',
          href: '/super-admin',
          icon: Shield,
          requireSuperAdmin: true
        }
      ]
    }
  ];

  const toggleExpanded = (itemName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API fails
      localStorage.clear();
      navigate('/login');
    }
  };

  const handleNavigation = (href, mobile = false) => {
    // Check if super admin needs tenant context for this route
    if (authUser?.super_admin && !hasActiveTenantContext) {
      const exemptPaths = ['/super-admin', '/tenant', '/login', '/'];
      if (!exemptPaths.includes(href) && !href.startsWith('/super-admin')) {
        alert('Please select a tenant first to access this section.');
        return;
      }
    }
    
    navigate(href);
    if (mobile) setSidebarOpen(false);
  };

  const notifications = [
    { id: 1, text: 'Low inventory alert: Batteries', type: 'warning', time: '5m ago' },
    { id: 2, text: 'New stock adjustment recorded', type: 'info', time: '15m ago' },
    { id: 3, text: 'Inventory movement: 50 tires received', type: 'success', time: '30m ago' },
    { id: 4, text: 'New order received #1234', type: 'success', time: '1h ago' },
    { id: 5, text: 'Customer payment pending', type: 'info', time: '2h ago' }
  ];

  const Navigation = ({ mobile = false }) => (
    <nav className={mobile ? "mt-5 px-2 space-y-1" : "mt-5 flex-1 px-2 space-y-1"}>
      {navigationItems.map((item) => {
        // Skip items that require super admin if user is not super admin
        if (item.requireSuperAdmin && !user?.super_admin) {
          return null;
        }
        
        return (
        <div key={item.name}>
          {item.children ? (
            <>
              <button
                onClick={() => toggleExpanded(item.name)}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  expandedMenus[item.name]
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center">
                  <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.name}
                </div>
                <div className="flex items-center">
                  {item.badge && (
                    <span className={`mr-2 px-2 py-1 text-xs font-medium rounded-full ${
                      item.badgeType === 'warning'
                        ? 'bg-yellow-100 text-yellow-800'
                        : item.badgeType === 'success'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                  {expandedMenus[item.name] ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </div>
              </button>
              {expandedMenus[item.name] && (
                <div className="mt-1 space-y-1">
                  {item.children.map((child) => {
                    if (child.requireSuperAdmin && !user?.super_admin) {
                      return null;
                    }
                    return (
                      <button
                        key={child.name}
                        onClick={() => handleNavigation(child.href, mobile)}
                        className="w-full flex items-center px-11 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <child.icon className="mr-3 h-4 w-4" />
                        <span className="flex-1 text-left">{child.name}</span>
                        {child.badge && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                            {child.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <button
              onClick={() => handleNavigation(item.href, mobile)}
              className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                item.current
                  ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-700'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
              {item.name}
              {item.badge && (
                <span className="ml-auto px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                  {item.badge}
                </span>
              )}
            </button>
          )}
        </div>
        );
      })}
    </nav>
  );

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        <div
          className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ${
            sidebarOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setSidebarOpen(false)}
        />
        
        <div className={`relative flex-1 flex flex-col max-w-xs w-full bg-white transform transition-transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4">
              <div className="flex items-center">
                <Car className="h-8 w-8 text-blue-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">Rubbert</span>
              </div>
            </div>
            <Navigation mobile={true} />
          </div>
          
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <span className="text-white font-medium">
                    {user.name.charAt(0)}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{user.name}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 bg-white border-r border-gray-200">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <div className="flex items-center">
                  <Car className="h-8 w-8 text-blue-600" />
                  <div className="ml-2">
                    <h1 className="text-xl font-bold text-gray-900">Rubbert</h1>
                    <p className="text-xs text-gray-500">Tire Management System</p>
                  </div>
                </div>
              </div>
              <Navigation />
            </div>
            
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <div className="flex items-center w-full">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-white font-medium">
                      {user.name.charAt(0)}
                    </span>
                  </div>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-700">{user.name}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-2 p-2 text-gray-400 hover:text-gray-500"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top header */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="flex-1 px-4 flex justify-between items-center">
            <div className="flex-1 flex">
              <div className="w-full flex md:ml-0">
                <div className="relative w-full max-w-md text-gray-400 focus-within:text-gray-600">
                  <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none pl-3">
                    <Search className="h-5 w-5" />
                  </div>
                  <input
                    className="block w-full h-full pl-10 pr-3 py-2 border-transparent text-gray-900 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-0 focus:border-transparent sm:text-sm"
                    placeholder="Search"
                    type="search"
                  />
                </div>
              </div>
            </div>
            
            <div className="ml-4 flex items-center md:ml-6 space-x-3">
              {/* Tenant Switcher for Super Admin */}
              {authUser?.super_admin && (
                <div className="tenant-switcher">
                  <TenantSwitcher />
                </div>
              )}
              
              {/* Notifications */}
              <div className="relative notifications-dropdown">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 relative"
                >
                  <Bell className="h-6 w-6" />
                  <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500"></span>
                </button>
                
                {showNotifications && (
                  <div className="origin-top-right absolute right-0 mt-2 w-96 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.map(notif => (
                        <div key={notif.id} className="px-4 py-3 hover:bg-gray-50 border-b border-gray-100">
                          <p className="text-sm text-gray-800">{notif.text}</p>
                          <p className="text-xs text-gray-500 mt-1">{notif.time}</p>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 border-t border-gray-200">
                      <button className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium">
                        View all notifications
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* User menu */}
              <div className="relative user-dropdown">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-3 p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                >
                  <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user.name.charAt(0)}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-700">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </button>
                
                {showUserMenu && (
                  <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <LogOut className="mr-3 h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          {/* Super Admin Notice */}
          {authUser?.super_admin && !hasActiveTenantContext && (
            <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3">
              <div className="flex items-center">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    Super Admin Notice: Please select a tenant from the dropdown in the top navigation bar to view tenant-specific data.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default EnhancedDashboardLayout;